import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import FileDownload from 'js-file-download';
import queryString from 'query-string';
import Heading from '@tds/core-heading';
import Modal from '@tds/community-modal';
import InputGroup from '@tds/community-input-group';

import Filters from '../components/filters';
import { Button, ButtonWrapper, ExportButton, SearchWrapper } from './css';
import { ContentWrapper, Box } from '../../design/css';
import Table from '../../design/shared/table';
import request, {
  apiHost,
  USER_RIGHTS,
  config as requestConfig,
  isSalesManager,
  isOrderFulfillmentTeam
} from '../../utils/request';

import OrderListModel, {
  OrderHeaderModel,
  getOptions,
  RepModel,
  ChannelModel
} from './models';
import OrderDetail from '../orderDetails';
import MessageNotification from '../../messageNotification';
import ValidateLoader from '../../UI/validateLoader';
import { useLocalizedStringsContext } from '../../context/LocalizedStringsContext';
import { hasLength, getFormattedDate } from '../../utils/utils';
import {
  getBusinessAppFormStepFields,
  contentfulFormConfig
} from '../../contentful';
import {
  orderStatus,
  quoteStatus,
  keyModifyQuote,
  keyProceedWithOrder,
  keyViewOrder,
  keyDeleteOrder,
  orderDetailsForm,
  keySaveQuotePdf,
  keyContractResend,
  keyOrderReturn
} from '../../utils/constant';

import { getStatus, getLink } from '../../srtDashboard/utils';
import { DisabledAction } from '../../salesRepDashboard/css';

const connectionKey = 'BUSINESS_APP_ORDERS';

const initialFilterState = {
  status: '',
  startDate: null,
  endDate: null,
  filterCriteria: null,
  channelId: [],
  province: [],
  createdBy: '',
  productInOrderFor: ''
};

const Orders = ({ locale }) => {
  const history = useHistory();
  const location = useLocation();
  const { search } = location;
  const localizedStrings = useLocalizedStringsContext();
  const staticLabels = localizedStrings[connectionKey];
  const {
    general: { loading: loadingLabel },
    srtDashboard: { continueLabel, locked }
  } = localizedStrings;
  const {
    EXPORT,
    NO_ORDERS,
    LOADING_MESSAGE,
    ORDER_WITH,
    SUCCESS_MESSAGE,
    DELETE_MESSAGE,
    viewOrderButtonLabel,
    ordersTableTitle,
    proceedWithOrder,
    deleteOrderLabel,
    modifyQuote,
    modifyOrder,
    confirmationDialog,
    confirmationMessage,
    cancel,
    confirm,
    searchLabel,
    exportWarning,
    searchHint,
    saveQuote,
    requestOrderReturnButtonLabel,
    contractResendButtonLabel
  } = staticLabels;

  const [loading, setLoading] = useState(false);
  const [isExport, setIsExport] = useState(false);
  const [filterState, setFilterState] = useState(initialFilterState);
  const config = { ...contentfulFormConfig, locale };
  const {
    startDate,
    endDate,
    assignee,
    channelId,
    province,
    createdBy,
    filterCriteria,
    status,
    productInOrderFor
  } = filterState;
  const {
    incomplete,
    contractSent,
    contractSigned,
    orderSubmitted,
    contractExpired,
    orderCanceled
  } = orderStatus;
  const { approved } = quoteStatus;

  const [viewOrderId, setViewOrderId] = useState();
  const [data, setData] = useState();
  const [orderDetails, setOrderDetails] = useState(null);
  const [isOrderUpdated, setOrderUpdated] = useState();
  const [orderSummary, setOrderSummary] = useState(null);
  const [order, setOrder] = useState({});
  const [action, setAction] = useState();
  const [salesRepNames, setSalesRepNames] = useState(null);
  const [assignees, setAssignees] = useState(null);
  const [channels, setChannels] = useState(null);
  const [orderComments, setOrderComments] = useState(null);
  const [tableHeaders, setTableHeaders] = useState(
    new OrderHeaderModel(staticLabels)
  );
  const { page } = queryString.parse(search);
  const [searchString, setSearchString] = useState();
  const [message, setMessage] = useState(SUCCESS_MESSAGE);
  const profile = request.getProfile();
  const userProfileRole = profile && profile.role.name;
  const userRights = userProfileRole && USER_RIGHTS[userProfileRole];
  const isSalesSupport = profile && [11, 12].includes(profile.role_id);
  // const isAdminAndOrderFulfillmentTeam =
  //   profile && [1, 3].includes(profile.role_id);
  const pageSize = 50;
  const pageQuery = `pageNumber=${page || 1}&pageSize=${pageSize}`;
  const isOrderCancelAvailable = profile && [3, 5].includes(profile.role_id);

  const allowedStatusForDashboardSOV = [
    incomplete,
    contractSent,
    contractSigned,
    orderCanceled,
    contractExpired,
    approved,
    orderSubmitted
  ];

  const allowedStatusForDashboardOrderFulfillment = [
    contractSent,
    contractSigned,
    orderCanceled,
    contractExpired,
    approved,
    orderSubmitted
  ];

  const getStatusQuery = () => {
    let statusArr;
    if (isOrderFulfillmentTeam)
      statusArr = allowedStatusForDashboardOrderFulfillment;
    else statusArr = allowedStatusForDashboardSOV;

    return statusArr
      .reduce((acc, curr) => {
        acc.push(`status=${curr}`);
        return acc;
      }, [])
      .join('&');
  };

  let ordersUrl = `/orders?type=BA&${pageQuery}&${getStatusQuery()}`;
  const isFilterApplied = hasLength(
    endDate,
    startDate,
    assignee,
    channelId,
    province,
    createdBy,
    filterCriteria,
    status,
    productInOrderFor
  );

  const filterData = {
    salesRepNames,
    assignees,
    channels
  };

  useEffect(() => {
    setTableHeaders(new OrderHeaderModel(staticLabels));
  }, [localizedStrings]);

  const fetchDataFromUrl = url => {
    return request.get(url);
  };
  const fetchOrders = () => {
    return request.get(ordersUrl);
  };

  const fetchData = () => {
    setLoading(true);
    fetchOrders().then(ordersResp => {
      const {
        data: { totalCount: ordersCount, results: orders }
      } = ordersResp;
      setData({
        orders,
        count: ordersCount
      });
      setLoading(false);
    });
  };

  const getFilterPayload = () => {
    const payload = {
      ...filterState,
      createdBy: filterState.createdBy.length && filterState.createdBy[0].id,
      province: filterState.province.map(row => row.value),
      channelId:
        isSalesSupport &&
        !filterState.channelId.length &&
        profile.channels.length
          ? profile.channels.map(row => row.id)
          : filterState.channelId.map(row => row.value),
      type: 'BA'
    };
    delete payload.assignee;

    if (filterState.status === '') {
      const statusFilters = isOrderFulfillmentTeam
        ? allowedStatusForDashboardOrderFulfillment
        : allowedStatusForDashboardSOV;
      payload.status = [...statusFilters];
    }
    if (filterState.startDate)
      payload.startDate = getFormattedDate(filterState.startDate);
    if (filterState.endDate)
      payload.endDate = getFormattedDate(filterState.endDate);
    return payload;
  };

  const filterOrders = (isResetMode = false) => {
    if (isResetMode) {
      request
        .get(ordersUrl)
        .then(({ data: { results: orders, totalCount } }) => {
          setData({ ...data, orders, count: totalCount });
          setLoading(false);
        });
    } else if (searchString) {
      ordersUrl = ordersUrl.concat(`&q=${searchString}`);
      request
        .get(ordersUrl)
        .then(({ data: { results: orders, totalCount } }) => {
          setData({ ...data, orders, count: totalCount });
          setLoading(false);
        });
    } else {
      const payload = getFilterPayload();
      setLoading(true);
      request
        .post('/orders/filter', payload)
        .then(({ data: { results: orders, totalCount } }) => {
          setData({ ...data, orders, count: totalCount });
          setLoading(false);
        });
    }
  };

  const updateFilters = (key, value) => {
    setFilterState({ ...filterState, [key]: value });
  };

  const onClickView = id => {
    setLoading(true);
    getBusinessAppFormStepFields(config, setOrderSummary);
    Promise.all([
      fetchDataFromUrl(`/orders/${id}`),
      fetchDataFromUrl(`/orders/${id}/comments`)
    ]).then(([orderResp, commentsResp]) => {
      const { data: orders } = orderResp;
      const {
        data: { results: comments }
      } = commentsResp;
      setOrderDetails(orders);
      setOrderComments(comments);
      setOrderUpdated(false);
      setLoading(false);
    });
  };

  const openOrder = id => {
    setOrderDetails(null);
    setViewOrderId(id);
  };

  const doExport = () => {
    const payload = getFilterPayload();
    setAction();
    setIsExport(true);
    axios
      .post(`${apiHost}/orders/export/excel`, payload, {
        ...requestConfig(),
        responseType: 'arraybuffer'
      })
      .then(response => response.data)
      .then(fileData => {
        setIsExport(false);
        FileDownload(fileData, 'orders.xlsx');
      })
      .catch(err => {
        // eslint-disable-next-line no-alert
        alert(err);
        setIsExport(false);
      });
  };

  const onClickExport = () => {
    if (!isExport) {
      if (!isFilterApplied) {
        setAction(EXPORT);
      } else {
        doExport();
      }
    }
  };

  const updateContractResendToTrue = row => {
    const body = {
      isContractResend: true
    };
    setLoading(true);
    request.put(`/orders/${row.id}`, body).then(() => {
      setOrder({ ...order, open: false, isUpdated: true });
      setLoading(false);
    });
  };

  const updateOrderReturnToTrue = row => {
    const body = {
      isOrderReturned: true
    };
    setLoading(true);
    request.put(`/orders/${row.id}`, body).then(() => {
      setOrder({ ...order, open: false, isUpdated: true });
      setLoading(false);
    });
  };

  const onSave = value => {
    setMessage(SUCCESS_MESSAGE);
    setOrderUpdated(value);
  };

  const resetFilters = () => {
    setFilterState({
      ...initialFilterState,
      province: [],
      channelId: [],
      assignee: [],
      channelOrgCode: [],
      outletCode: [],
      salesRepName: [],
      status: ''
    });
  };

  const actionsSalesManager = [
    {
      key: keyViewOrder,
      label: viewOrderButtonLabel,
      onClick: row => openOrder(row.id),
      forStatus: [
        'quoteApproved',
        'contractSent',
        'contractSigned',
        'orderCanceled',
        'orderSubmitted',
        'contractExpired',
        'orderIncomplete'
      ]
    }
  ];

  const getActionsForSalesManager = row => {
    const { id, isLockedBy, isOrderReturned, isContractResend } = row;
    const { statusId } = getStatus(row);
    const { email } = profile;

    if (isLockedBy && isLockedBy !== email)
      return <p style={{ fontSize: '14px', color: '#adadad' }}>{locked}</p>;

    let actionsList;

    if (isLockedBy && isLockedBy !== email)
      actionsList = actionsSalesManager.filter(
        ({ key }) => key === keyViewOrder
      );
    else
      actionsList = actionsSalesManager.filter(({ forStatus }) =>
        forStatus.includes(statusId)
      );

    return actionsList.map(({ label, onClick }, index) => {
      const key = `order${id}actionLink${index}`;
      if (isOrderReturned && label === requestOrderReturnButtonLabel) {
        return <DisabledAction>{label}</DisabledAction>;
      }
      if (isContractResend && label === contractResendButtonLabel) {
        return <DisabledAction>{label}</DisabledAction>;
      }
      if (onClick)
        return (
          <Button key={key} onClick={() => onClick(row)}>
            {label}
          </Button>
        );
      return getLink(id, label);
    });
  };

  const getActions = row => {
    const { status: currentStatus, id } = row;

    return (
      <>
        <Button key={id} onClick={() => openOrder(row.id)}>
          {viewOrderButtonLabel}
        </Button>
        {isOrderCancelAvailable &&
          [contractSent, contractSigned].includes(currentStatus) && (
            <Button
              key={id}
              onClick={() => {
                setAction(deleteOrderLabel);
                setOrder({ open: true, orderDetails: row });
              }}
            >
              {deleteOrderLabel}
            </Button>
          )}
      </>
    );
  };

  const redirect = (row, formName, step) => {
    history.push({
      pathname: `/${row.locale}/${row.categoryName}/${formName}`,
      search: `?orderId=${row.id}&step=${step}`
    });
    history.go();
  };

  const deleteOrder = () => {
    return request.delete(`/orders/${order.orderDetails.id}`).then(() => {
      setOrder({ ...order, open: false, isUpdated: true });
      setOrderUpdated({ orderId: order.orderDetails.orderId, isUpdated: true });
      setMessage(DELETE_MESSAGE);
      fetchData();
    });
  };

  const onConfirm = () => {
    switch (action) {
      case modifyQuote:
        redirect(order.orderDetails, orderDetailsForm, 1);
        break;
      case proceedWithOrder:
        redirect(order.orderDetails, orderDetailsForm, 2);
        break;
      case deleteOrderLabel:
        deleteOrder();
        break;
      case EXPORT:
        doExport();
        break;
      case keyOrderReturn:
        updateOrderReturnToTrue(order.orderDetails);
        break;
      case keyContractResend:
        updateContractResendToTrue(order.orderDetails);
        break;
      default:
    }
  };

  useEffect(() => {
    if (isOrderUpdated) {
      setOrderUpdated(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!orderDetails) {
      if (isFilterApplied || searchString) {
        filterOrders();
      } else {
        fetchData();
      }
    }
  }, [orderDetails, page]);

  useEffect(() => {
    Promise.allSettled([
      fetchDataFromUrl('/salesRepUsers'),
      fetchDataFromUrl('/sovAgentAndAdminUsers'),
      fetchDataFromUrl('/channels')
    ]).then(
      ([
        // usersResp,
        salesRepNameResp,
        assigneeResponse,
        channelsResp
      ]) => {
        const {
          value: { data: repNames }
        } = salesRepNameResp;
        const {
          value: { data: assigneeData }
        } = assigneeResponse;
        const {
          value: {
            data: { channels: channelList }
          }
        } = channelsResp;
        setSalesRepNames(getOptions(repNames, RepModel));
        setAssignees(getOptions(assigneeData, RepModel));
        setChannels(getOptions(channelList, ChannelModel));
      }
    );
  }, []);

  useEffect(() => {
    if (viewOrderId) {
      onClickView(viewOrderId);
    } else if (isFilterApplied || searchString) {
      filterOrders();
    } else {
      fetchData();
    }
  }, [viewOrderId, isOrderUpdated]);

  return (
    <ContentWrapper>
      {loading ? (
        <ValidateLoader message={LOADING_MESSAGE} />
      ) : (
        <>
          {viewOrderId ? (
            <>
              {!orderDetails ? (
                <ValidateLoader message={LOADING_MESSAGE} />
              ) : (
                <>
                  {orderSummary && userRights && (
                    <OrderDetail
                      open={!!orderDetails}
                      setOpen={() => setViewOrderId('')}
                      order={orderDetails}
                      onSave={onSave}
                      userRights={userRights}
                      assignees={assignees}
                      comments={orderComments}
                      summary={orderSummary}
                      isSalesManager={isSalesManager}
                      isSalesSupport={isSalesSupport}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Box pb={48}>
                <Filters
                  updateFilters={(key, value) => updateFilters(key, value)}
                  filterState={filterState}
                  onApplyFilters={filterOrders}
                  onResetFilters={resetFilters}
                  data={filterData}
                  staticLabels={staticLabels}
                />
              </Box>
              {isOrderUpdated && isOrderUpdated.isUpdated && (
                <Box pt={20} pb={20}>
                  <MessageNotification
                    variant="success"
                    message={`${ORDER_WITH}${isOrderUpdated.orderId} ${message}`}
                  />
                </Box>
              )}
              <SearchWrapper>
                <InputGroup
                  label={searchLabel}
                  hint={searchHint}
                  defaultValue={searchString}
                  onClearButtonClick={() => {
                    setSearchString('');
                    filterOrders(true);
                  }}
                  onChange={e => setSearchString(e.target.value)}
                  onButtonClick={() => filterOrders()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') filterOrders();
                  }}
                />
              </SearchWrapper>
              {data && data.orders && data.orders.length ? (
                <>
                  <Table
                    title={ordersTableTitle}
                    header={tableHeaders}
                    data={data.orders}
                    actions={
                      isSalesManager ? getActionsForSalesManager : getActions
                    }
                    dataModel={OrderListModel}
                    currentPage={parseInt(page || 1, 10)}
                    count={data.count}
                    step={pageSize}
                    actionsDisplay="grid"
                  />
                  <ButtonWrapper>
                    <ExportButton
                      onClick={onClickExport}
                      className="buttonload"
                    >
                      <i
                        className={
                          isExport
                            ? 'fa  fa-circle-o-notch fa-spin'
                            : 'fa fa-spin'
                        }
                      />
                      {isExport ? loadingLabel : EXPORT}
                    </ExportButton>
                  </ButtonWrapper>
                </>
              ) : (
                <Heading level="h4">{NO_ORDERS}</Heading>
              )}
            </>
          )}
        </>
      )}
      <Modal
        heading={confirmationDialog}
        bodyText={`${
          action === EXPORT ? exportWarning : ''
        }${confirmationMessage}?`}
        confirmCTAText={confirm}
        cancelCTAText={cancel}
        onConfirm={() => onConfirm()}
        onClose={() => {
          if (action === EXPORT) {
            setAction();
          } else {
            setOrder({ ...order, open: false });
          }
        }}
        isOpen={(action && order.open) || action === EXPORT}
      />
    </ContentWrapper>
  );
};

Orders.propTypes = {
  locale: PropTypes.string.isRequired
};

export default Orders;
